/**
 * The subsidised delivery fee (W10-DELIVERY-FOOD-LEVERS, D494, ADR-0111; lever L6) inside Admin › Finanças › Ofertas de incentivo: the governed bounds, and the form that PROPOSES a
 * subsidy of the delivery fee of a food order for a zone or the whole market (OFF unless the market publishes `incentives.deliveryFeeSubsidyPolicy`). A DIFFERENT operator approves it in
 * the offers table above and either may end it there. Money is typed through the design-system `MoneyInput` in the policy's currency; every code shown goes through the vocabulary.
 */
import { type ReactElement, useState } from 'react';
import type { ActiveIncentivesListView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, Field, FormRow, MoneyInput, Notice, Panel, Row, Select, Stack, Text, TextInput } from '@vello/web-theme';
import { EMPTY_DELIVERY_SUBSIDY_DRAFT, bpToPercentText, deliverySubsidyDraftIssue, deliverySubsidyProposalBody, percentTextToBp, policyStateTone, windowChoices, type DeliverySubsidyDraft } from '../../src/index';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

export function DeliverySubsidyOffersPanel(props: {
  readonly view: ActiveIncentivesListView;
  readonly market: string;
  readonly mayManage: boolean;
  readonly busy: boolean;
  /** The market's drawn, approved zones (id and name) beside the whole market. */
  readonly zones: readonly (readonly [string, string])[];
  readonly submit: (body: ReturnType<typeof deliverySubsidyProposalBody>) => Promise<boolean>;
}): ReactElement | null {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [draft, setDraft] = useState<DeliverySubsidyDraft>(EMPTY_DELIVERY_SUBSIDY_DRAFT);
  const [shareText, setShareText] = useState('');
  const policy = props.view.deliverySubsidyPolicy;
  if (policy === undefined) return null;
  const open = policy.state === 'ACTIVE' && props.view.fund.state === 'ACTIVE' && policy.currency !== null && isIso4217Currency(policy.currency);
  const currency = policy.currency ?? '';
  const money = (m: { readonly amountMinor: string; readonly currency: string } | undefined): string =>
    m === undefined || !/^\d+$/.test(m.amountMinor) || !isIso4217Currency(m.currency) ? '—' : formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });
  const issue = open ? deliverySubsidyDraftIssue(draft, policy, props.view.proposalReasons) : undefined;
  const windows = windowChoices(policy.maxWindowHours ?? 0);

  const send = async (): Promise<void> => {
    if (issue !== undefined || !open) return;
    if (await props.submit(deliverySubsidyProposalBody(props.market, draft, new Date().toISOString()))) {
      setDraft(EMPTY_DELIVERY_SUBSIDY_DRAFT);
      setShareText('');
    }
  };

  return (
    <Panel>
      <Stack gap={3}>
        <h3>{t('subsidy.title')}</h3>
        <Text role="caption" tone="tertiary">{t('subsidy.subtitle')}</Text>
        <Row gap={2} align="center" wrap>
          <Text role="caption" tone="tertiary">{t('subsidy.policy')}</Text>
          <Badge tone={policyStateTone(policy.state)}>{v('incentive_fund.policy_state', policy.state)}</Badge>
          <Text role="caption" tone="tertiary">{t('offers.policy.version', { version: policy.policyVersion })}</Text>
        </Row>
        {!open ? (
          <Notice tone="warning">{t('subsidy.needs')}</Notice>
        ) : (
          <Text role="caption" tone="tertiary">
            <span className="tnum">
              {t('subsidy.bounds', {
                percent: bpToPercentText(policy.maxSubsidyBp ?? 0), cap: money(policy.maxSubsidyPerOrder), day: policy.maxPerCustomerPerDay ?? 0, month: policy.maxPerCustomerPerMonth ?? 0, hours: policy.maxWindowHours ?? 0,
                verticals: (policy.verticals ?? []).map((x) => v('delivery_subsidy.vertical', x)).join(', '),
              })}
            </span>
          </Text>
        )}
        {props.mayManage && open && (
          <>
            <FormRow action={<Button type="button" disabled={issue !== undefined || props.busy} onClick={() => void send()}>{t('subsidy.submit')}</Button>}>
              <Field label={t('offers.propose.zone')} htmlFor="subsidy-zone">
                <Select id="subsidy-zone" value={draft.zoneId} onChange={(ev) => setDraft((d) => ({ ...d, zoneId: ev.target.value }))}>
                  <option value="MARKET">{t('offers.zone.market')}</option>
                  {props.zones.map(([id, name]) => (
                    <option key={id} value={id}>{t('offers.zone.drawn', { name })}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('subsidy.share')} htmlFor="subsidy-share" hint={t('subsidy.share.hint', { percent: bpToPercentText(policy.maxSubsidyBp ?? 0) })}>
                <TextInput
                  id="subsidy-share"
                  inputMode="decimal"
                  maxLength={6}
                  value={shareText}
                  onChange={(ev) => {
                    setShareText(ev.target.value);
                    setDraft((d) => ({ ...d, subsidyBp: percentTextToBp(ev.target.value) }));
                  }}
                />
              </Field>
              <MoneyInput id="subsidy-cap" label={t('subsidy.cap')} currency={currency} locale={lang} valueMinor={draft.perOrderCapMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, perOrderCapMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
              <MoneyInput id="subsidy-budget" label={t('subsidy.budget')} currency={currency} locale={lang} valueMinor={draft.budgetMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, budgetMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
              <Field label={t('offers.propose.window')} htmlFor="subsidy-window">
                <Select id="subsidy-window" value={draft.windowHours === undefined ? '' : String(draft.windowHours)} onChange={(ev) => setDraft((d) => ({ ...d, windowHours: ev.target.value === '' ? undefined : Number(ev.target.value) }))}>
                  <option value="">{t('offers.propose.window.choose')}</option>
                  {windows.map((h) => (
                    <option key={h} value={String(h)}>{t('offers.propose.window.hours', { hours: h })}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('offers.propose.reason')} htmlFor="subsidy-reason">
                <Select id="subsidy-reason" value={draft.reasonCode} onChange={(ev) => setDraft((d) => ({ ...d, reasonCode: ev.target.value }))}>
                  <option value="">{t('offers.propose.reason.choose')}</option>
                  {props.view.proposalReasons.map((r) => (
                    <option key={r} value={r}>{v('active_incentive.reason', r)}</option>
                  ))}
                </Select>
              </Field>
            </FormRow>
            {issue !== undefined && draft !== EMPTY_DELIVERY_SUBSIDY_DRAFT && <Text role="caption" tone="tertiary">{t(`subsidy.issue.${issue}`)}</Text>}
          </>
        )}
      </Stack>
    </Panel>
  );
}
